/**
 * The Jr Staff board forum, client side - the parent board's sibling.
 *
 * Bobbie, 2026-08-29, choosing this over raising the 25-participant thread
 * cap after Kim's meeting-message report: "They can just have a jr staff
 * board. That would be fine... In fact I think she would prefer that."
 * Membership follows her earlier words for the group this replaces: "put
 * all the parents that have junior staff as well as junior staff into that
 * group" - so every active staff account (Jr Staff's own accounts included,
 * live by role) plus the guardians of every dancer the Director marked Jr
 * Staff (via the derived roll at jrStaffBoardMembership/current).
 *
 * NOTHING HERE IS A PERMISSION. Membership is decided in
 * shared/firestore.rules by isJrStaffBoardMember(). The helpers below exist
 * so nobody is shown a composer that would only refuse them - a courtesy,
 * not a boundary. The thread and reply SHAPE is the parent board's exactly,
 * so every pure helper in parentBoardThreads.ts (ordering, hearts, search,
 * edit labels) works on both boards unchanged.
 */
import { useEffect, useRef, useState } from 'react';
import { getAuth, type User } from 'firebase/auth';
import {
  addDoc,
  collection,
  deleteField,
  doc,
  increment,
  limit,
  limitToLast,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import { removeBoardItem } from './boardModeration';
import { newestReplyWindow } from './boardReplyWindow';
import { watchBoardRollUids } from './boardMembership';
import {
  FIRESTORE_PERMISSION_DENIED_CODE,
  LISTENER_RETRY_MAX_ATTEMPTS,
  firestoreListenerErrorCode,
  isTransientFirestoreListenerErrorCode,
  listenerRetryDelayMs,
} from './boardMembershipOverlay';
import { notifyUsers } from './notifications';
import { saveMessageWithOptionalAttachments } from './messageAttachmentWrite';
import { hasRichMessageFormatting, plainMessageText } from '../../netlify/functions/lib/messageFormatting.mjs';
import { isMessageFileContentType, type MessageAttachment } from './threads';
import {
  PARENT_BOARD_HEART,
  PARENT_BOARD_MAX_BODY,
  PARENT_BOARD_MAX_TITLE,
  type ParentBoardAuthorRole,
  type ParentBoardReply,
  type ParentBoardThread,
} from './parentBoardThreads';
import { tsToMillis } from './tonight';

export const JR_STAFF_BOARD_THREADS = 'jrStaffBoardThreads';
export const JR_STAFF_BOARD_MEMBERSHIP = 'jrStaffBoardMembership';
/** Same caps and the same honest cap note as parentBoard.ts's own. */
export const JR_STAFF_BOARD_THREAD_LIMIT = 100;
export const JR_STAFF_BOARD_REPLY_LIMIT = 200;
/**
 * There is one Jr Staff board, so the attachment lane's targetId is a fixed
 * sentinel rather than a document id, the same shape COMP_BOARD_CURRENT uses
 * in compBoard.ts. Must match JR_STAFF_BOARD_TARGET in
 * hub/netlify/functions/lib/messageAttachmentWrite.mjs.
 */
export const JR_STAFF_BOARD_CURRENT = 'current';

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function authorRoleOf(value: unknown): ParentBoardAuthorRole {
  return value === 'staff' ? 'staff' : 'family';
}

function reactionsFrom(value: unknown): Record<string, string> {
  const raw = record(value);
  const out: Record<string, string> = {};
  for (const [uid, emoji] of Object.entries(raw)) {
    if (typeof emoji === 'string' && emoji) out[uid] = emoji;
  }
  return out;
}

/**
 * Attachments, read side only. The WRITE side is server-owned (see
 * messageAttachmentWrite), so this does not revalidate a shape the admin
 * transaction already proved; it refuses anything malformed enough to crash
 * a render, and nothing more. Copied from compBoard.ts's own attachmentsFrom
 * rather than shared: two board files deliberately do not depend on each
 * other, the same reasoning compTip's identical rules branch is a copy of
 * compBoard's rather than an import.
 */
function attachmentsFrom(value: unknown): MessageAttachment[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 4).flatMap((raw) => {
    const row = record(raw);
    const contentType = text(row.contentType);
    const url = text(row.url);
    const id = text(row.id);
    if (!id || !url || !contentType) return [];
    const kind = contentType.startsWith('video/')
      ? ('video' as const)
      : contentType.startsWith('image/')
        ? ('image' as const)
        : ('file' as const);
    if (kind === 'file' && !isMessageFileContentType(contentType)) return [];
    return [{
      id,
      kind,
      url,
      name: text(row.name, 'Attachment'),
      contentType,
      ...(text(row.storagePath) ? { storagePath: text(row.storagePath) } : {}),
    }];
  });
}

function threadFrom(id: string, data: Record<string, unknown>): ParentBoardThread {
  return {
    id,
    authorUid: text(data.authorUid),
    authorName: text(data.authorName, 'A board member'),
    authorRole: authorRoleOf(data.authorRole),
    title: text(data.title, 'Untitled thread'),
    body: text(data.body),
    reactions: reactionsFrom(data.reactions),
    replyCount: typeof data.replyCount === 'number' ? data.replyCount : 0,
    lastReplyAt: tsToMillis(data.lastReplyAt),
    createdAt: tsToMillis(data.createdAt),
    removedAt: tsToMillis(data.removedAt),
    editedAt: tsToMillis(data.editedAt),
    attachments: attachmentsFrom(data.attachments),
  };
}

function replyFrom(id: string, data: Record<string, unknown>): ParentBoardReply {
  return {
    id,
    authorUid: text(data.authorUid),
    authorName: text(data.authorName, 'A board member'),
    authorRole: authorRoleOf(data.authorRole),
    text: text(data.text),
    reactions: reactionsFrom(data.reactions),
    createdAt: tsToMillis(data.createdAt),
    removedAt: tsToMillis(data.removedAt),
    editedAt: tsToMillis(data.editedAt),
    attachments: attachmentsFrom(data.attachments),
  };
}

/** The whole board, newest thread first on the wire, re-sorted by activity. */
export function watchJrStaffBoard(
  onThreads: (threads: ParentBoardThread[], fromCache: boolean) => void,
  onError: (error: Error) => void,
  // On for the board page, whose ?thread=&reply= deep link must not give up
  // on a thread a cached first snapshot predates: the listener then also
  // fires when the SERVER confirms, and `fromCache` says which snapshot this
  // is (refute round on PR #1251). Off for a list-only caller.
  trackServer = false
): Unsubscribe {
  if (!db) throw new Error('Firestore not initialized.');
  return onSnapshot(
    query(
      collection(db, JR_STAFF_BOARD_THREADS),
      orderBy('createdAt', 'desc'),
      limit(JR_STAFF_BOARD_THREAD_LIMIT)
    ),
    { includeMetadataChanges: trackServer },
    (snap) => onThreads(
      snap.docs.map((row) => threadFrom(row.id, record(row.data()))),
      snap.metadata.fromCache
    ),
    (error) => onError(error instanceof Error ? error : new Error(String(error)))
  );
}

/**
 * ONE ROW, LIVE, BY ITS ID, for a deep link to something older than the
 * listener's newest page (bug B29; lib/boardLinkedPost.ts). Same collection,
 * same board-member rule as the listener, so it shows nothing the viewer could
 * not already see on the board. The first answer is one document read; after
 * that it carries hearts, edits and removals like any post on the board. The
 * row is null when the document does not exist.
 */
export function watchJrStaffBoardThread(
  id: string,
  onRow: (row: ParentBoardThread | null) => void,
  onError: (error: Error) => void
): Unsubscribe {
  if (!db) throw new Error('Firestore not initialized.');
  return onSnapshot(
    doc(db, JR_STAFF_BOARD_THREADS, id),
    (snap) => onRow(snap.exists() ? threadFrom(snap.id, record(snap.data())) : null),
    (error) => onError(error instanceof Error ? error : new Error(String(error)))
  );
}

/**
 * One thread's NEWEST replies, shown oldest first because a conversation
 * reads down (Bobbie, 2026-09-28; lib/boardReplyWindow.ts). It used to read
 * the oldest window, hiding the newest replies of a long thread. `hasEarlier`
 * says earlier replies exist; "Show earlier replies" re-asks with more.
 */
export function watchJrStaffBoardReplies(
  threadId: string,
  onReplies: (replies: ParentBoardReply[], hasEarlier: boolean) => void,
  onError: (error: Error) => void,
  size: number = JR_STAFF_BOARD_REPLY_LIMIT
): Unsubscribe {
  if (!db) throw new Error('Firestore not initialized.');
  return onSnapshot(
    query(
      collection(db, JR_STAFF_BOARD_THREADS, threadId, 'replies'),
      orderBy('createdAt', 'asc'),
      limitToLast(size + 1)
    ),
    (snap) => {
      const newest = newestReplyWindow(snap.docs, size);
      onReplies(newest.rows.map((row) => replyFrom(row.id, record(row.data()))), newest.hasEarlier);
    },
    (error) => onError(error instanceof Error ? error : new Error(String(error)))
  );
}

export type JrStaffBoardMembership = 'unknown' | 'yes' | 'no';

/**
 * Whether an account is on the derived roll - the door question.
 *
 * A LIVE LISTENER, NOT A ONE-SHOT READ (PR #807 round 11, Codex P2). The
 * original shape here was a single getDoc on mount: a guardian added to the
 * roll (an overlay add, or the next hourly sweep) after this hook had
 * already resolved never flipped the answer, so Messages.tsx's
 * jrStaffRollRaw stayed 'no' for the rest of the session - the Jr Staff
 * discussion query never opened for her even though the server's own
 * notification linked her straight back to Messages, a dead end until she
 * reloaded. Every call site here (this board's own door, the two family-home
 * doors, and the announcement audience in Messages.tsx) wants the CURRENT
 * answer for as long as the page stays open, not just the answer at mount,
 * so none of them lose anything by going live - see each call site's own
 * comment for why a stale answer was actively wrong there.
 *
 * REUSES watchBoardRollUids (lib/boardMembership.ts) rather than opening a
 * second onSnapshot on the same doc: that helper already subscribes to
 * jrStaffBoardMembership/current live for the Manage members sheet and
 * NewMessage.tsx's own announcement-reach count, so this is the SAME
 * listener shape and the SAME doc, not a parallel implementation that could
 * drift from it.
 *
 * FAIL CLOSED on a listener error, same direction as before (and the same
 * one useBoardMembershipEffectiveMember's own overlay read documents): a
 * transient read failure is not proof of exclusion, but this hook is a
 * courtesy door, never the actual boundary - shared/firestore.rules'
 * isJrStaffBoardMember() re-proves the real answer server-side regardless
 * of what this hook could fetch.
 *
 * CACHED-ONLY DATA STAYS 'unknown' (Codex P1, PR #807 round 16). Local
 * persistence means watchBoardRollUids' onData can fire with a snapshot
 * the SERVER has not actually confirmed yet - a guardian just removed from
 * the roll, reloading before the server round-trip lands, would otherwise
 * read a cached pre-removal list as an immediate 'yes' and open the Jr
 * Staff discussion query / render cached announcements for a slice she can
 * no longer see, the exact fail-open shape round 13 already closed for the
 * overlay hook. Every consumer of this hook already treats 'unknown'
 * identically to 'no' (see each call site's own comment - all of them gate
 * on the strict `=== 'yes'`, never on `!== 'no'`), so withholding the
 * yes/no verdict until fromCache is false costs nothing: the answer simply
 * arrives one snapshot later, same as any other unsettled read.
 *
 * CODE-AWARE ERROR HANDLING, NOT A BLANKET PRESERVE (Codex P2a, PR #807
 * round 17). Round 16's "preserve a confirmed answer across an error" has
 * a real gap Codex found by reading Firestore's own contract: an
 * onSnapshot ERROR is TERMINAL, the listener is dead, and nothing
 * resubscribes it on its own. "Preserve forever" and "preserve across a
 * momentary blip" look identical in the state shape but are very
 * different promises - the first quietly breaks every removal-propagation
 * claim this PR makes (a door that stays open until a full remount, no
 * matter how long ago the roll actually changed), which is stale UI, not
 * a leak (the rules re-check the same predicate on the real read), but is
 * still wrong. See lib/boardMembershipOverlay.ts's own doc comment on
 * firestoreListenerErrorCode for the full reasoning shared with
 * boardMembership.ts's matching fix this same round; the short version:
 * permission-denied is a dead end no resubscribe can clear (fails closed
 * immediately, no retry, overriding even a confirmed 'yes'); a transient
 * code (network hiccup, momentary server condition) leaves the confirmed
 * answer untouched and resubscribes automatically, bounded, with backoff;
 * an unrecognized code is treated like permission-denied, conservatively.
 */
export function useJrStaffBoardMembership(
  uid: string | null,
  enabled: boolean
): JrStaffBoardMembership {
  return useJrStaffBoardMembershipState(uid, enabled).membership;
}

/**
 * The same live answer, plus whether it is a REAL one (Bobbie, 2026-10-31,
 * menu pins auto-clear; lib/menuPinsSettled.ts). Every failure above still
 * reads 'no' for the door, but a menu that takes pins off for rows it does not
 * draw must not read "could not ask" as "not on the roll". `settled` is true
 * only while the answer is a server-confirmed snapshot: false while waiting,
 * while a dropped listener is being retried, and after any failure.
 */
export function useJrStaffBoardMembershipState(
  uid: string | null,
  enabled: boolean
): { membership: JrStaffBoardMembership; settled: boolean } {
  const [membership, setMembershipValue] = useState<JrStaffBoardMembership>('unknown');
  const [settled, setSettled] = useState(false);
  // Every write of the answer says whether it is a real one.
  const setMembership = (value: JrStaffBoardMembership, real = false) => {
    setMembershipValue(value);
    setSettled(real);
  };
  // Bumped to force the effect below to tear down and rebuild its
  // subscription - the SAME unsubscribe/resubscribe shape
  // lib/compBoard.ts's useCompBoardRosterNames `refetch` uses for its own
  // attempt counter, just driven by an automatic scheduled retry here
  // instead of a person's tap (this hook has no visible "Try again" of its
  // own to drive one).
  const [attempt, setAttempt] = useState(0);
  // Which (uid, enabled) question the ref-tracked state below belongs to -
  // NOT an effect dependency itself, just how the effect tells "this run is
  // a genuinely new question" apart from "this run is my own retry".
  const identity = useRef<string | null>(null);
  // Whether THIS identity has ever delivered a server-confirmed (not
  // cache-only) snapshot - read inside onError to decide preserve vs
  // fail-closed. Reset only on a genuinely new identity, never on a retry
  // of the SAME identity (a retry exists precisely to keep trying for the
  // uid already being asked about).
  const confirmed = useRef(false);
  // Consecutive transient-error retry attempts for THIS identity. Reset
  // only on a genuinely new identity; a real success (fromCache:false)
  // also resets it, so a later, unrelated blip gets its own full budget.
  const retryCount = useRef(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const currentIdentity = enabled && uid ? `${String(enabled)}:${uid}` : null;
    if (identity.current !== currentIdentity) {
      identity.current = currentIdentity;
      confirmed.current = false;
      retryCount.current = 0;
    }
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
    if (!enabled || !uid) {
      setMembership('unknown');
      return;
    }
    if (retryCount.current === 0) {
      // A genuinely fresh mount for this identity, not a retry of it - a
      // retry must NOT flash the display back to 'unknown' while it works
      // in the background; the whole point is that the confirmed answer
      // (if any) stays visible through a transient blip.
      setMembership('unknown');
    }
    return watchBoardRollUids(
      'jrStaffBoard',
      (uids, fromCache) => {
        if (fromCache) return; // Still pending - see the docstring above.
        confirmed.current = true;
        retryCount.current = 0;
        setMembership(uids.includes(uid) ? 'yes' : 'no', true);
      },
      (err) => {
        const code = firestoreListenerErrorCode(err);
        if (code === FIRESTORE_PERMISSION_DENIED_CODE) {
          // Not a blip, and no resubscribe can clear it. The roll doc is
          // readable by any ACTIVE user (shared/firestore.rules gates it on
          // isActiveUserByDoc(), NOT on roll membership - watchBoardRollUids'
          // own docstring says the same), so a denial says the caller is not
          // an active user at all - signed out, deactivated, retired - which
          // a fresh listener would only meet again. Fails closed at once,
          // overriding even a previously confirmed 'yes'. Full reasoning, and
          // the rule text it was checked against, in lib/
          // boardMembershipOverlay.ts's firestoreListenerErrorCode doc
          // comment.
          confirmed.current = false;
          setMembership('no');
          return;
        }
        if (isTransientFirestoreListenerErrorCode(code) && retryCount.current < LISTENER_RETRY_MAX_ATTEMPTS) {
          retryCount.current += 1;
          const delay = listenerRetryDelayMs(retryCount.current);
          retryTimer.current = setTimeout(() => setAttempt((n) => n + 1), delay);
          // Leave `membership` exactly as it is - a confirmed answer stays
          // visible through the blip; an unconfirmed one stays 'unknown'
          // (still pending, same as before any snapshot ever landed). It is
          // not a settled answer while the listener is down, though.
          setSettled(false);
          return;
        }
        // Retries exhausted, or a code this hook does not recognize as
        // transient - fail closed, overriding whatever was being
        // preserved. An indefinitely retried, indefinitely stale confirmed
        // answer is its own kind of wrong.
        confirmed.current = false;
        setMembership('no');
      }
    );
  }, [uid, enabled, attempt]);

  // Cleanup on unmount only - the effect's own cleanup above already
  // clears a pending timer on every rerun; this catches the LAST one, when
  // there is no further rerun to do it.
  useEffect(() => () => {
    if (retryTimer.current) clearTimeout(retryTimer.current);
  }, []);

  return { membership, settled: settled && membership !== 'unknown' };
}

export interface JrStaffBoardAuthor {
  uid: string;
  name: string;
  role: ParentBoardAuthorRole;
}

export interface JrStaffBoardThreadOptions {
  /**
   * The Jr Staff GROUP MESSAGE mirror (Bobbie, 2026-08-29: "Can I get a jr
   * staff board too that messages also post too?") has been through three
   * rounds of getting this wrong.
   *
   * ROUND ONE passed plain `false` here, reasoning the group send had
   * already notified the SAME whole-board audience. FALSE (Bobbie that
   * night: no notification for a board post at all) - a group thread's
   * audience is its own participants, never the board's staff-by-role
   * members like the Director sitting outside it.
   *
   * ROUND TWO (Codex, PR #801 review) fixed that by excluding every
   * thread participant instead - reasoning the message send had pinged
   * them. ALSO FALSE, one level deeper: ordinary GROUP MESSAGES NOTIFY
   * NOBODY BY DESIGN (Bobbie, 2026-08-08: "Replies to a group we don't
   * need") except an @mention or an ack-required must-read. Excluding
   * "the whole thread" therefore excluded people the message send never
   * rang at all, so a plain mirrored send left the actual Jr Staff
   * members with no message alert AND no board alert.
   *
   * TWO RULINGS MEET HERE, BOTH HOLD. Group chat stays quiet by design
   * (2026-08-08) - nothing here changes that. But a mirrored post IS a
   * board post, and the board rings everyone, every time (2026-08-29
   * evening) - so the board ping is most members' ONE AND ONLY bell for
   * a mirrored send, never a second one to skip.
   *
   * THE FIX (round three) passes `{ mirrorOfThreadId, mirrorOfMessageId }`
   * naming the exact message just sent and mirrored. The server reads
   * that message's own stored fields and derives the exclusion from what
   * the message lane can PROVE it already rang - every participant if the
   * message was ack-required, only the message's own @mentions otherwise,
   * nobody at all for a plain send - never "everyone in the thread". See
   * resolveJrStaffBoardMirrorExclusion in notify.mjs for the full
   * derivation and trust boundary. A thread STARTED on the board itself
   * keeps the default true - there is no messaging send to exclude.
   */
  notify?: boolean | { mirrorOfThreadId: string; mirrorOfMessageId: string };
  /** Photos, videos and files already uploaded through the shared gateway. */
  attachments?: MessageAttachment[];
  /** Required when there are attachments: the server lane verifies the caller. */
  user?: User | null;
}

/**
 * TWO ROADS, one shape, the same split compBoard.ts's postToCompBoard uses.
 * A text-only thread is written straight from here. A thread carrying
 * attachments goes through the SAME server lane every other attachment-
 * bearing message in the Hub uses (messageAttachmentWrite), which consumes
 * each upload reservation in the same transaction that creates the thread.
 * There is deliberately no second uploader and no client-written attachments
 * field: the jrStaffBoardThreads create rule does not name one, and the
 * admin lane does not need one named.
 */
export async function startJrStaffBoardThread(
  author: JrStaffBoardAuthor,
  title: string,
  body: string,
  options?: JrStaffBoardThreadOptions
): Promise<string> {
  if (!db) throw new Error('Firestore not initialized.');
  const cleanTitle = title.trim();
  const cleanBody = body.trim();
  if (!cleanTitle) throw new Error('Give your thread a title first.');
  if (!cleanBody) throw new Error('Say a little more first.');
  if (cleanTitle.length > PARENT_BOARD_MAX_TITLE) {
    throw new Error('That title is a bit long. Trim it a little.');
  }
  if (plainMessageText(cleanBody).length > PARENT_BOARD_MAX_BODY) {
    throw new Error('That is longer than the board takes. Trim it a little.');
  }
  // TWO ROADS to threadId (#802, board media): a text-only thread is
  // written straight from here; a thread carrying attachments goes
  // through the admin lane (messageAttachmentWrite), which consumes each
  // upload reservation in the same transaction that creates the thread.
  const attachments = options?.attachments ?? [];
  let threadId: string;
  if (attachments.length > 0 || hasRichMessageFormatting(cleanBody)) {
    if (!options?.user) throw new Error('Sign in again to send that attachment.');
    const ref = doc(collection(db, JR_STAFF_BOARD_THREADS));
    await saveMessageWithOptionalAttachments({
      user: options.user,
      action: 'jrStaffBoardThread',
      targetId: JR_STAFF_BOARD_CURRENT,
      writeId: ref.id,
      attachments,
      payload: { title: cleanTitle, body: cleanBody },
    });
    threadId = ref.id;
  } else {
    // The create carries the words and nothing else, same as the parent
    // board: reactions, lastReplyAt and removedAt are not createable fields.
    const created = await addDoc(collection(db, JR_STAFF_BOARD_THREADS), {
      authorUid: author.uid,
      authorName: author.name,
      authorRole: author.role,
      title: cleanTitle,
      body: cleanBody,
      replyCount: 0,
      createdAt: serverTimestamp(),
    });
    threadId = created.id;
  }
  // Same mirror-id-aware notify regardless of which road produced
  // threadId - see JrStaffBoardThreadOptions.notify's own comment for the
  // three-round derivation. The mirror caller (NewMessage.tsx) is
  // text-only by construction (an attachment-only send has no words to
  // mirror), so notifyOption is only ever an object on the addDoc road in
  // practice, but the extraction itself does not need to assume that.
  const notifyOption = options?.notify;
  if (notifyOption !== false) {
    const mirrorOfThreadId = typeof notifyOption === 'object' ? notifyOption.mirrorOfThreadId : undefined;
    const mirrorOfMessageId = typeof notifyOption === 'object' ? notifyOption.mirrorOfMessageId : undefined;
    await notifyJrStaffBoard(threadId, undefined, mirrorOfThreadId, mirrorOfMessageId);
  }
  return threadId;
}

/**
 * Tell the board. No recipients are sent, same as the parent board and for a
 * stronger version of its reason: a FAMILY member of this board cannot read
 * `users` at all, and even a staff composer cannot read the guardian half of
 * the audience reliably. The server derives it from its own reads through
 * the jrStaffBoard authority lane, and for EVERY call here - a fresh thread
 * or a reply alike - that server-derived audience is the whole board
 * (Bobbie, 2026-08-29 evening, verbatim: "Everyone needs to get a
 * notification anytime. Anything is posted on there and replies"). Unlike
 * the parent board, a reply here does not narrow to the conversation. Never
 * throws: saved is saved.
 *
 * `mirrorOfThreadId`/`mirrorOfMessageId` are only ever set by the Jr Staff
 * group message mirror in startJrStaffBoardThread above - see its own
 * comment for what the server derives from them and why "the whole
 * thread" is never a valid exclusion.
 */
async function notifyJrStaffBoard(
  threadId: string,
  replyId?: string,
  mirrorOfThreadId?: string,
  mirrorOfMessageId?: string
): Promise<void> {
  try {
    await notifyUsers([], {
      kind: 'board',
      title: replyId ? 'New reply on the Jr Staff board' : 'New on the Jr Staff board',
      body: 'Open the Jr Staff board to read it.',
      link: '/jr-board',
    }, {
      jrStaffBoard: {
        threadId,
        ...(replyId ? { replyId } : {}),
        ...(mirrorOfThreadId ? { mirrorOfThreadId } : {}),
        ...(mirrorOfMessageId ? { mirrorOfMessageId } : {}),
      },
    });
  } catch {
    // Saved is saved.
  }
}

export interface JrStaffBoardReplyOptions {
  /** Photos, videos and files already uploaded through the shared gateway. */
  attachments?: MessageAttachment[];
  /** Required when there are attachments: the server lane verifies the caller. */
  user?: User | null;
}

/**
 * Reply, then bump the activity denorm - same order and reasoning as
 * parentBoard, and the same TWO ROADS split startJrStaffBoardThread uses
 * just above. A reply carrying attachments goes through the admin lane in
 * one transaction (create the reply, bump replyCount/lastReplyAt), so the
 * two-write client shape below stays exactly for the text-only road.
 */
export async function replyToJrStaffBoardThread(
  author: JrStaffBoardAuthor,
  threadId: string,
  body: string,
  options?: JrStaffBoardReplyOptions
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const trimmed = body.trim();
  if (!trimmed) throw new Error('Write something first.');
  if (plainMessageText(trimmed).length > PARENT_BOARD_MAX_BODY) {
    throw new Error('That is longer than the board takes. Trim it a little.');
  }
  const database = db;
  const attachments = options?.attachments ?? [];
  let replyId: string;
  if (attachments.length > 0 || hasRichMessageFormatting(trimmed)) {
    if (!options?.user) throw new Error('Sign in again to send that attachment.');
    const ref = doc(collection(database, JR_STAFF_BOARD_THREADS, threadId, 'replies'));
    await saveMessageWithOptionalAttachments({
      user: options.user,
      action: 'jrStaffBoardReply',
      targetId: threadId,
      writeId: ref.id,
      attachments,
      payload: { text: trimmed },
    });
    replyId = ref.id;
  } else {
    const reply = await addDoc(collection(database, JR_STAFF_BOARD_THREADS, threadId, 'replies'), {
      authorUid: author.uid,
      authorName: author.name,
      authorRole: author.role,
      text: trimmed,
      createdAt: serverTimestamp(),
    });
    await updateDoc(doc(database, JR_STAFF_BOARD_THREADS, threadId), {
      replyCount: increment(1),
      lastReplyAt: serverTimestamp(),
    });
    replyId = reply.id;
  }
  await notifyJrStaffBoard(threadId, replyId);
}

/** One heart per person, and taking it back is your key leaving the map. */
export async function heartJrStaffBoardThread(
  threadId: string,
  uid: string,
  hearted: boolean
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, JR_STAFF_BOARD_THREADS, threadId), {
    [`reactions.${uid}`]: hearted ? PARENT_BOARD_HEART : deleteField(),
  });
}

export async function heartJrStaffBoardReply(
  threadId: string,
  replyId: string,
  uid: string,
  hearted: boolean
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, JR_STAFF_BOARD_THREADS, threadId, 'replies', replyId), {
    [`reactions.${uid}`]: hearted ? PARENT_BOARD_HEART : deleteField(),
  });
}

/** Author-only rewrites, staff-or-author removal: the rules decide, these ask. */
export async function editJrStaffBoardThread(
  threadId: string,
  title: string,
  body: string
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const cleanTitle = title.trim();
  const cleanBody = body.trim();
  if (!cleanTitle) throw new Error('Give your thread a title first.');
  if (!cleanBody) throw new Error('Say a little more first.');
  if (cleanTitle.length > PARENT_BOARD_MAX_TITLE) {
    throw new Error('That title is a bit long. Trim it a little.');
  }
  if (plainMessageText(cleanBody).length > PARENT_BOARD_MAX_BODY) {
    throw new Error('That is longer than the board takes. Trim it a little.');
  }
  if (hasRichMessageFormatting(cleanBody)) {
    const user = getAuth().currentUser;
    if (!user) throw new Error('Sign in again to edit this thread.');
    await saveMessageWithOptionalAttachments({
      user,
      action: 'jrStaffBoardEditThread',
      targetId: threadId,
      writeId: threadId,
      attachments: [],
      payload: { title: cleanTitle, body: cleanBody },
    });
  } else {
    await updateDoc(doc(db, JR_STAFF_BOARD_THREADS, threadId), {
      title: cleanTitle,
      body: cleanBody,
      editedAt: serverTimestamp(),
    });
  }
}

export async function editJrStaffBoardReply(
  threadId: string,
  replyId: string,
  text: string
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const trimmed = text.trim();
  if (!trimmed) throw new Error('Write something first.');
  if (plainMessageText(trimmed).length > PARENT_BOARD_MAX_BODY) {
    throw new Error('That is longer than the board takes. Trim it a little.');
  }
  if (hasRichMessageFormatting(trimmed)) {
    const user = getAuth().currentUser;
    if (!user) throw new Error('Sign in again to edit this reply.');
    await saveMessageWithOptionalAttachments({
      user,
      action: 'jrStaffBoardEditReply',
      targetId: threadId,
      writeId: replyId,
      attachments: [],
      payload: { text: trimmed },
    });
  } else {
    await updateDoc(doc(db, JR_STAFF_BOARD_THREADS, threadId, 'replies', replyId), {
      text: trimmed,
      editedAt: serverTimestamp(),
    });
  }
}

/**
 * The server moves the thread or reply off the board into a server-only
 * archive (lib/boardArchive.mjs, 2026-10-24), so no member's phone downloads
 * its words any more; the words are kept there. The author, on
 * either of her sign-ins, or a Director, and nobody else (Bobbie, 2026-10-01,
 * verbatim: "Only directors can remove other people's posts"). Through the
 * server since that evening (netlify/functions/boardModeration.mjs), which
 * writes removedBy beside removedAt; the rules have no client lane for it.
 */
export async function removeJrStaffBoardThread(threadId: string): Promise<void> {
  await removeBoardItem('jrStaffBoard', threadId, null);
}

export async function removeJrStaffBoardReply(
  threadId: string,
  replyId: string
): Promise<void> {
  await removeBoardItem('jrStaffBoard', threadId, replyId);
}
